// GPS track smoothing for walks.
//
// Why this exists: walk distance was a raw haversine sum over every GPS
// sample. Background GPS on a phone wobbles ±15-50m even while stationary,
// and occasionally jumps hundreds of metres on a single bad fix. Summing
// those raw deltas inflates a 3km stroll into ~9km and draws a zig-zag path.
//
// The fix is a 2-D constant-velocity Kalman filter with an RTS (Rauch-Tung-
// Striebel) backward smoother. Each fix is weighted by its own reported GPS
// accuracy (a ~1σ radius in metres): tight fixes pull the track hard, loose
// fixes barely nudge it. We do NOT reject points — a bad jump is attenuated
// (the model won't believe an implausible velocity) rather than dropped, so
// the path stays continuous and honest about where the phone thought it was.
//
// Both the rendered polyline and the reported distance come out of the same
// smoothed track, so the map and the number always agree. The backend runs
// the identical algorithm (wellnuo-backend src/utils/gpsFilter.js) over the
// stored points so day/week/month totals match this per-walk view. Keep the
// two implementations in sync if you touch the model or the constants.

// --- Tunables -------------------------------------------------------------
// Process-noise spectral density (m²/s³) for the continuous white-noise
// acceleration model. This is the one knob that trades smoothing vs.
// responsiveness: lower = smoother path but cuts corners on real turns,
// higher = follows the raw fixes (and their noise) more closely. Started at
// 0.1 (recovered a noise-dominated straight walk to ~1.15x true vs ~3.5x for
// the raw haversine sum); dropped to 0.03 on 2026-06-02 because real walks in
// built-up areas were still visibly jagged from background-GPS/WiFi multipath.
// 0.03 trusts the constant-velocity motion model more and the jumpy fixes
// less — smoother path, at the cost of rounding genuinely sharp turns a little
// more. Real walks turn far less often than the synthetic loop, so the added
// corner-cutting bias is small. MUST match the backend copy (gpsFilter.js).
const ACCEL_PSD = 0.03
// Assumed accuracy (metres, 1σ) for points with no `acc` field.
const DEFAULT_ACC_M = 25
// Floor on accuracy so an over-optimistic "±1m" fix can't make the filter
// trust a measurement absolutely (R→0) and chase noise.
const MIN_ACC_M = 3
// Initial velocity variance ((m/s)²): we don't know the starting speed, so
// start loose and let the first few fixes settle it.
const INIT_VEL_VAR = 25

export interface LatLngAcc {
    lat: number
    lng: number
    acc?: number | null
    ts: number
}

export interface SmoothedTrack {
    // Smoothed positions, one per input point, in input order (sorted by ts).
    points: { lat: number; lng: number }[]
    // Total distance along the smoothed track, in metres.
    distanceMeters: number
}

// One axis of the constant-velocity Kalman filter. State is [position,
// velocity]; covariance P is the symmetric 2×2 [[p00,p01],[p01,p11]].
// Position and velocity in the east/north metre frame; the two axes are
// independent under this model (block-diagonal F, Q, R), so we run this twice.
function smoothAxis(z: number[], r: number[], dts: number[]): number[] {
    const n = z.length
    if (n === 0) return []
    if (n === 1) return [z[0]]

    // Forward pass — store filtered (posterior) and predicted (prior) state
    // + covariance at every step; the RTS smoother needs the priors.
    const xf: [number, number][] = new Array(n)   // filtered mean
    const Pf: number[][] = new Array(n)            // filtered cov [p00,p01,p11]
    const xp: [number, number][] = new Array(n)    // predicted mean (prior)
    const Pp: number[][] = new Array(n)            // predicted cov (prior)

    // Init at the first measurement; velocity unknown.
    xf[0] = [z[0], 0]
    Pf[0] = [r[0], 0, INIT_VEL_VAR]
    xp[0] = xf[0]
    Pp[0] = Pf[0]

    for (let i = 1; i < n; i++) {
        const dt = dts[i]
        const [pp, pv] = xf[i - 1]
        const [a, b, c] = Pf[i - 1] // P = [[a,b],[b,c]]

        // Predict: x' = F x, with F = [[1,dt],[0,1]].
        const xPredPos = pp + dt * pv
        const xPredVel = pv

        // P' = F P Fᵀ + Q.
        // F P Fᵀ for [[1,dt],[0,1]]:
        //   [[a + 2·dt·b + dt²·c,  b + dt·c],
        //    [b + dt·c,            c        ]]
        const dt2 = dt * dt, dt3 = dt2 * dt
        // Continuous white-noise-acceleration Q:
        //   q·[[dt³/3, dt²/2],[dt²/2, dt]]
        const q00 = ACCEL_PSD * dt3 / 3
        const q01 = ACCEL_PSD * dt2 / 2
        const q11 = ACCEL_PSD * dt
        const pa = a + 2 * dt * b + dt2 * c + q00
        const pb = b + dt * c + q01
        const pc = c + q11

        xp[i] = [xPredPos, xPredVel]
        Pp[i] = [pa, pb, pc]

        // Update with measurement z[i] (H = [1,0], measurement var r[i]).
        const S = pa + r[i]              // innovation variance
        const k0 = pa / S                // Kalman gain (position)
        const k1 = pb / S                // Kalman gain (velocity)
        const y = z[i] - xPredPos        // innovation

        xf[i] = [xPredPos + k0 * y, xPredVel + k1 * y]
        // P = (I - K H) P'. With H=[1,0]:
        //   [[(1-k0)·pa,        (1-k0)·pb       ],
        //    [pb - k1·pa,       pc - k1·pb      ]]
        // Symmetrise the off-diagonal (the two forms are equal in exact
        // arithmetic; average guards against drift).
        const na = (1 - k0) * pa
        const nb = ((1 - k0) * pb + (pb - k1 * pa)) / 2
        const nc = pc - k1 * pb
        Pf[i] = [na, nb, nc]
    }

    // Backward RTS smoother. xs/Ps seeded from the last filtered estimate.
    const xs: [number, number][] = new Array(n)
    xs[n - 1] = xf[n - 1]
    let Ps = Pf[n - 1]

    for (let i = n - 2; i >= 0; i--) {
        const dt = dts[i + 1]
        const [fa, fb, fc] = Pf[i]        // filtered cov at i
        const [pa, pb, pc] = Pp[i + 1]    // predicted cov at i+1 (prior)

        // Smoother gain C = Pf[i] Fᵀ Pp[i+1]⁻¹.
        // Fᵀ = [[1,0],[dt,1]]; Pf Fᵀ = [[fa+dt·fb, fb],[fb+dt·fc, fc]].
        const m00 = fa + dt * fb, m01 = fb
        const m10 = fb + dt * fc, m11 = fc
        // Inverse of Pp[i+1] = [[pa,pb],[pb,pc]].
        const det = pa * pc - pb * pb
        if (Math.abs(det) < 1e-12) { xs[i] = xf[i]; Ps = Pf[i]; continue }
        const inv00 = pc / det, inv01 = -pb / det, inv11 = pa / det
        // C = M · inv.
        const c00 = m00 * inv00 + m01 * inv01
        const c01 = m00 * inv01 + m01 * inv11
        const c10 = m10 * inv00 + m11 * inv01
        const c11 = m10 * inv01 + m11 * inv11

        // x_s[i] = x_f[i] + C (x_s[i+1] - x_p[i+1]).
        const dpos = xs[i + 1][0] - xp[i + 1][0]
        const dvel = xs[i + 1][1] - xp[i + 1][1]
        xs[i] = [
            xf[i][0] + c00 * dpos + c01 * dvel,
            xf[i][1] + c10 * dpos + c11 * dvel,
        ]
        // P_s[i] = P_f[i] + C (P_s[i+1] - P_p[i+1]) Cᵀ — only needed to feed
        // the next iteration's gain. Ps as [sa,sb,sc].
        const [sa, sb, sc] = Ps
        const da = sa - pa, db = sb - pb, dc = sc - pc
        // D = (P_s[i+1]-P_p[i+1]); C D Cᵀ.
        // CD = [[c00·da+c01·db, c00·db+c01·dc],[c10·da+c11·db, c10·db+c11·dc]]
        const cd00 = c00 * da + c01 * db, cd01 = c00 * db + c01 * dc
        const cd10 = c10 * da + c11 * db, cd11 = c10 * db + c11 * dc
        // (CD)Cᵀ, Cᵀ=[[c00,c10],[c01,c11]].
        const e00 = cd00 * c00 + cd01 * c01
        const e01 = cd00 * c10 + cd01 * c11
        const e11 = cd10 * c10 + cd11 * c11
        Ps = [fa + e00, fb + (e01) , fc + e11]
    }

    return xs.map(s => s[0])
}

const EARTH_M_PER_DEG_LAT = 111320 // ~constant; good enough at walk scale.

/**
 * Smooth a GPS track and report its distance. Returns the smoothed positions
 * (one per input point, sorted by ts) and the total distance along them.
 *
 * Points with <2 samples can't be smoothed — returned as-is with distance 0.
 */
export function smoothTrack(input: LatLngAcc[]): SmoothedTrack {
    const pts = [...input].sort((a, b) => a.ts - b.ts)
    if (pts.length < 2) {
        return { points: pts.map(p => ({ lat: p.lat, lng: p.lng })), distanceMeters: 0 }
    }

    // Project to a local east/north metre frame centred on the first fix.
    // Linearisation is exact enough over a walk (≤ a few km).
    const lat0 = pts[0].lat
    const mPerDegLng = EARTH_M_PER_DEG_LAT * Math.cos(lat0 * Math.PI / 180)
    const east = pts.map(p => (p.lng - pts[0].lng) * mPerDegLng)
    const north = pts.map(p => (p.lat - lat0) * EARTH_M_PER_DEG_LAT)

    // Measurement variance per point = (accuracy radius)². Clamp the radius
    // to a sane floor so a bogus "±1m" can't dominate.
    const r = pts.map(p => {
        const acc = (typeof p.acc === 'number' && isFinite(p.acc) && p.acc > 0) ? p.acc : DEFAULT_ACC_M
        const clamped = Math.max(MIN_ACC_M, acc)
        return clamped * clamped
    })

    // Δt between consecutive fixes (seconds); guard non-positive timestamps.
    const dts = pts.map((p, i) => i === 0 ? 0 : Math.max(0.001, (p.ts - pts[i - 1].ts) / 1000))

    const smE = smoothAxis(east, r, dts)
    const smN = smoothAxis(north, r, dts)

    const out: { lat: number; lng: number }[] = new Array(pts.length)
    let distanceMeters = 0
    for (let i = 0; i < pts.length; i++) {
        out[i] = {
            lat: lat0 + smN[i] / EARTH_M_PER_DEG_LAT,
            lng: pts[0].lng + smE[i] / mPerDegLng,
        }
        if (i > 0) {
            const de = smE[i] - smE[i - 1]
            const dn = smN[i] - smN[i - 1]
            distanceMeters += Math.sqrt(de * de + dn * dn)
        }
    }

    return { points: out, distanceMeters }
}
